const BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

function getToken() {
  return localStorage.getItem('appee_token');
}

// Une tentative unique. Marque err.transient sur les hoquets réseau/timeout/5xx.
async function _attempt(path, options, timeoutMs) {
  const token = getToken();
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);

  let res;
  try {
    res = await fetch(`${BASE}/api${path}`, {
      headers: {
        'Content-Type':  'application/json',
        'Accept':        'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
        ...options.headers,
      },
      credentials: 'include',
      signal: controller.signal,
      ...options,
    });
  } catch (err) {
    clearTimeout(timer);
    if (err.name === 'AbortError') {
      const e = new Error('Délai dépassé — vérifiez votre connexion.');
      e.transient = true;
      throw e;
    }
    err.transient = true; // erreur réseau
    throw err;
  }
  clearTimeout(timer);

  // 401 = token expired/invalid — clear it and redirect to login
  if (res.status === 401) {
    localStorage.removeItem('appee_token');
    window.location.href = '/login';
    throw new Error('Session expirée. Veuillez vous reconnecter.');
  }

  // 204 No Content
  if (res.status === 204) return null;

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    const msg = data.message || data.error || `Erreur ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    if (res.status >= 500) err.transient = true; // hoquet serveur (cold-start…)
    if (data.quota_exceeded) {
      err.quotaExceeded = true;
      err.quotaType  = data.quota_type;
      err.quotaUsed  = data.used;
      err.quotaLimit = data.limit;
    }
    if (data.trial_expired) {
      err.trialExpired = true;
    }
    throw err;
  }

  return data;
}

async function request(path, options = {}, timeoutMs = 15000) {
  // Réessaie UNE fois sur un hoquet transitoire (5xx / réseau / timeout), mais
  // SEULEMENT pour les GET (idempotents) — jamais un POST/PUT/DELETE, pour ne
  // pas risquer un double envoi. Rend les démarrages à froid backend invisibles.
  const method = (options.method || 'GET').toUpperCase();
  const canRetry = method === 'GET';
  try {
    return await _attempt(path, options, timeoutMs);
  } catch (err) {
    if (canRetry && err.transient) {
      await new Promise(r => setTimeout(r, 400));
      return await _attempt(path, options, timeoutMs);
    }
    throw err;
  }
}

async function upload(path, formData) {
  const token = getToken();
  const res = await fetch(`${BASE}/api${path}`, {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    credentials: 'include',
    body: formData,
  });

  if (res.status === 401) {
    localStorage.removeItem('appee_token');
    window.location.href = '/login';
    throw new Error('Session expirée.');
  }

  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.message || data.error || `Erreur ${res.status}`);
  return data;
}

export const api = {
  // ── Auth ──────────────────────────────────────────────────────────────────
  login:          (email, password)           => request('/auth/login',           { method: 'POST', body: JSON.stringify({ email, password }) }),
  register:       (data)                      => request('/auth/register',        { method: 'POST', body: JSON.stringify(data) }),
  logout:         ()                          => request('/auth/logout',          { method: 'POST' }),
  me:             ()                          => request('/auth/me'),
  forgotPassword: (email)                     => request('/auth/forgot-password', { method: 'POST', body: JSON.stringify({ email }) }),
  resetPassword:  (token, email, password)    => request('/auth/reset-password',  { method: 'POST', body: JSON.stringify({ token, email, password, password_confirmation: password }) }),

  // ── Dashboard ─────────────────────────────────────────────────────────────
  dashboard:          () => request('/dashboard'),
  dashboardAiInsight: () => request('/dashboard/ai-insight'),
  getOpportunities:   () => request('/opportunities'),
  getGuide:           () => request('/guide'),
  getJournal:         () => request('/dashboard/journal'),

  // ── Billing (Stripe self-service) ─────────────────────────────────────────
  getBilling:   ()             => request('/billing'),
  checkoutPlan: (plan)         => request('/billing/checkout/plan', { method: 'POST', body: JSON.stringify({ plan }) }),
  checkoutPack: (type, index, returnPath) => request('/billing/checkout/pack', { method: 'POST', body: JSON.stringify({ type, index, ...(returnPath ? { return_path: returnPath } : {}) }) }),
  billingPortal: ()            => request('/billing/portal'),

  // ── Contacts ──────────────────────────────────────────────────────────────
  getContacts:   (params = {}) => {
    const qs = new URLSearchParams({ per_page: 500, ...params }).toString();
    return request(`/contacts?${qs}`);
  },
  getContact:    (id)          => request(`/contacts/${id}`),
  createContact: (data)        => request('/contacts', { method: 'POST', body: JSON.stringify(data) }),
  updateContact: (id, data)    => request(`/contacts/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteContact:     (id)      => request(`/contacts/${id}`,  { method: 'DELETE' }),
  deleteAllContacts: ()        => request('/contacts/all',    { method: 'DELETE' }),
  setExpertMode: (enabled)     => request('/settings/expert-mode', { method: 'POST', body: JSON.stringify({ enabled }) }),
  saveOnboardingState: (state) => request('/settings/onboarding', { method: 'POST', body: JSON.stringify(state) }),
  getConsentSummary:  ()       => request('/contacts/consent/summary'),
  confirmConsentBulk: ()       => request('/contacts/consent/confirm', { method: 'POST' }),
  exportContacts: ()           => `${BASE}/api/contacts/export/csv?token=${getToken()}`,

  // ── Birthdays ─────────────────────────────────────────────────────────────
  getBirthdays:   ()   => request('/birthdays'),
  sendBirthday:   (id) => request(`/birthdays/send/${id}`, { method: 'POST' }),
  sendAllBirthdays: () => request('/birthdays/send-all',   { method: 'POST' }),

  // ── Campaigns ─────────────────────────────────────────────────────────────
  getCampaigns:    ()       => request('/campaigns'),
  getCampaign:     (id)     => request(`/campaigns/${id}`),
  createCampaign:  (data)   => request('/campaigns', { method: 'POST', body: JSON.stringify(data) }),
  updateCampaign:  (id, data) => request(`/campaigns/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCampaign:  (id)     => request(`/campaigns/${id}`, { method: 'DELETE' }),
  sendCampaign:    (id)     => request(`/campaigns/${id}/send`,     { method: 'POST' }),
  scheduleCampaign:(id, at) => request(`/campaigns/${id}/schedule`, { method: 'POST', body: JSON.stringify({ scheduled_at: at }) }),
  campaignStats:         (id) => request(`/campaigns/${id}/stats`),
  generateCampaignReport:(id) => request(`/campaigns/${id}/report`, { method: 'POST' }),
  previewSegment:       (filters)   => request('/campaigns/preview-segment', { method: 'POST', body: JSON.stringify(filters) }),
  uploadCampaignImage:  (formData)  => upload('/campaigns/upload-image', formData),

  // ── Templates ─────────────────────────────────────────────────────────────
  getTemplates:   ()         => request('/templates'),
  createTemplate: (data)     => request('/templates', { method: 'POST', body: JSON.stringify(data) }),
  updateTemplate: (id, data) => request(`/templates/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteTemplate: (id)       => request(`/templates/${id}`, { method: 'DELETE' }),

  // ── Imports ───────────────────────────────────────────────────────────────
  getImports:      ()           => request('/imports'),
  previewImport:   (formData)   => upload('/imports/preview', formData),
  processImport:   (formData)   => upload('/imports/process', formData),

  // ── Reminders ─────────────────────────────────────────────────────────────
  getReminders:    ()         => request('/reminders'),
  createReminder:  (data)     => request('/reminders', { method: 'POST', body: JSON.stringify(data) }),
  updateReminder:  (id, data) => request(`/reminders/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteReminder:  (id)       => request(`/reminders/${id}`, { method: 'DELETE' }),

  // ── Strategy ──────────────────────────────────────────────────────────────
  analyzeStrategy: (objective)  => request('/strategy/analyze', { method: 'POST', body: JSON.stringify({ objective }) }),
  getActionPlans:  ()           => request('/strategy/action-plans'),
  launchAction:    (id)         => request(`/strategy/action-plans/${id}/launch`,  { method: 'POST' }),
  dismissAction:   (id)         => request(`/strategy/action-plans/${id}/dismiss`, { method: 'POST' }),

  // ── Strategy Instances ────────────────────────────────────────────────────
  getStrategyInstances:    ()                   => request('/strategy-instances'),
  saveStrategyInstance:    (s)                  => request('/strategy-instances', { method: 'POST', body: JSON.stringify({ instanceId: s.instanceId, data: s }) }),
  updateStrategyInstance:  (instanceId, data)   => request(`/strategy-instances/${instanceId}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteStrategyInstance:  (instanceId)         => request(`/strategy-instances/${instanceId}`, { method: 'DELETE' }),

  // ── Company Profile ───────────────────────────────────────────────────────
  getCompany:    ()     => request('/settings/company'),
  saveCompany:   (data) => request('/settings/company', { method: 'PUT', body: JSON.stringify(data) }),
  uploadLogo:    (formData) => upload('/settings/company/logo', formData),
  analyzeSocial:    (network, url)      => request('/ai/analyze-social',    { method: 'POST', body: JSON.stringify({ network, url }) }),
  analyzeWebsite:   (url)               => request('/ai/analyze-website',   { method: 'POST', body: JSON.stringify({ url }) }),
  brandColorsFromSite: (url)            => request('/settings/brand-colors-from-site', { method: 'POST', body: JSON.stringify({ url }) }),
  findSocialPage:   (network, query)    => request('/ai/find-social-page',  { method: 'POST', body: JSON.stringify({ network, query }) }),
  marketingAudit: () => request('/ai/marketing-audit', { method: 'POST' }, 60000),
  getAuditHistory: () => request('/ai/marketing-audit/history'),

  // ── Contact Campaign Notes ────────────────────────────────────────────────
  getContactCampaignNotes:  (id)       => request(`/contacts/${id}/campaign-notes`),
  saveContactCampaignNotes: (id, data) => request(`/contacts/${id}/campaign-notes`, { method: 'PUT', body: JSON.stringify(data) }),

  // ── Analytics ─────────────────────────────────────────────────────────────
  getAnalytics:    () => request('/analytics'),
  getSegments:     () => request('/analytics/segments'),
  getEmailLogs:    () => request('/analytics/emails'),
  getMonthlyReport:    () => request('/analytics/report'),
  getBirthdayTrend:      () => request('/analytics/birthday-trend'),
  getAnalyticsInsights:  () => request('/ai/analytics-insights', { method: 'POST' }),

  // ── AI ────────────────────────────────────────────────────────────────────
  generateMessage:  (objective, segmentInfo, tone) =>
    request('/ai/generate-message', { method: 'POST', body: JSON.stringify({ objective, segment_info: segmentInfo, tone }) }),
  generateSmsMessage: (objective, tone, emailBody) =>
    request('/ai/generate-sms', { method: 'POST', body: JSON.stringify({ objective, tone, email_body: emailBody || null }) }),
  generateBirthday: (contactId) =>
    request('/ai/generate-birthday', { method: 'POST', body: JSON.stringify({ contact_id: contactId }) }),
  generateStrategy: (objective, stats) =>
    request('/ai/generate-strategy', { method: 'POST', body: JSON.stringify({ objective, stats }) }),
  chatMessage: (message, history, pageCtx = {}, timeoutMs = 15000) =>
    request('/ai/chat', { method: 'POST', body: JSON.stringify({ message, history, page_ctx: pageCtx }) }, timeoutMs),
  generateWizardStrategy: (prompt) =>
    request('/ai/generate-wizard-strategy', { method: 'POST', body: JSON.stringify({ prompt }) }, 60000),
  generateSocialPost: (data) =>
    request('/ai/generate-social-post', { method: 'POST', body: JSON.stringify(data) }, 30000),
  generateVisual: (data) =>
    request('/ai/generate-visual', { method: 'POST', body: JSON.stringify(data) }, 120000), // image IA = lent
  generateStrategic: (task, fullContext, extra = '') =>
    request('/ai/generate-strategic', { method: 'POST', body: JSON.stringify({ task, full_context: fullContext, extra }) }, 30000),
  generateAuditStrategy: (audit) =>
    request('/ai/generate-audit-strategy',  { method: 'POST', body: JSON.stringify({ audit }) }, 60000),

  // ── SMS ───────────────────────────────────────────────────────────────────
  sendSms:      (data)            => request('/sms/send',      { method: 'POST', body: JSON.stringify(data) }),
  sendSmsBulk:  (data)            => request('/sms/send-bulk', { method: 'POST', body: JSON.stringify(data) }),
  getSmsLogs:   ()                => request('/sms/logs'),
  getSmsBalance:()                => request('/sms/balance'),
  getSmsSettings:  ()             => request('/settings/sms'),
  saveSmsSettings: (data)         => request('/settings/sms', { method: 'POST', body: JSON.stringify(data) }),
  testSmsSend:     (phone)        => request('/settings/sms/test', { method: 'POST', body: JSON.stringify({ phone }) }),

  // ── WhatsApp Business (client — self-service) ────────────────────────────
  sendWhatsApp:           (data)  => request('/whatsapp/send',               { method: 'POST', body: JSON.stringify(data) }),
  getWhatsAppLogs:        ()      => request('/whatsapp/logs'),
  getWhatsAppLogStatus:   (id)    => request(`/whatsapp/logs/${id}`),
  getWhatsAppSettings:    ()      => request('/settings/whatsapp'),
  saveWhatsAppSettings:   (data)  => request('/settings/whatsapp',           { method: 'POST', body: JSON.stringify(data) }),
  testWhatsAppConnection: ()      => request('/settings/whatsapp/test',      { method: 'POST' }),
  deleteWhatsAppSettings: ()      => request('/settings/whatsapp',           { method: 'DELETE' }),

  // ── WhatsApp Business (admin — support tenant) ────────────────────────────
  adminGetTenantWhatsApp:    (id)        => request(`/admin/tenants/${id}/whatsapp`),
  adminSaveTenantWhatsApp:   (id, data)  => request(`/admin/tenants/${id}/whatsapp`,      { method: 'POST',   body: JSON.stringify(data) }),
  adminTestTenantWhatsApp:   (id)        => request(`/admin/tenants/${id}/whatsapp/test`, { method: 'POST' }),
  adminDeleteTenantWhatsApp: (id)        => request(`/admin/tenants/${id}/whatsapp`,      { method: 'DELETE' }),

  // ── Admin ─────────────────────────────────────────────────────────────────
  getAdminStats:        ()           => request('/admin/stats'),
  getAdminTenants:      ()           => request('/admin/tenants'),
  getAdminTenant:       (id)         => request(`/admin/tenants/${id}`),
  updateAdminTenant:    (id, data)   => request(`/admin/tenants/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
  addAdminCredits:      (id, data)   => request(`/admin/tenants/${id}/credits`, { method: 'POST', body: JSON.stringify(data) }),
  resetAdminUsage:      (id, data)   => request(`/admin/tenants/${id}/reset-usage`, { method: 'POST', body: JSON.stringify(data) }),
  setAdminTenantSending:(id, paused) => request(`/admin/tenants/${id}/sending`, { method: 'POST', body: JSON.stringify({ paused }) }),
  deleteAdminTenant:    (id, confirm)=> request(`/admin/tenants/${id}`, { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  getAdminLogs:         (params)     => request(`/admin/logs?${new URLSearchParams(params || {})}`),
  logFrontendError:     (data)       => request('/admin/log-error', { method: 'POST', body: JSON.stringify(data) }),
  getPlatformCosts:     ()               => request('/admin/platform-costs'),
  updatePlatformCredit: (service, data)  => request('/admin/platform-costs/' + service, { method: 'PATCH', body: JSON.stringify(data) }),
  getIntegrations:      ()               => request('/admin/integrations'),
  updateIntegration:    (service, data)  => request('/admin/integrations/' + service, { method: 'PATCH', body: JSON.stringify(data) }),
  testIntegration:      (service)        => request('/admin/integrations/' + service + '/test', { method: 'POST' }),

  // ── Settings ──────────────────────────────────────────────────────────────
  getSmtp:            () => request('/settings/smtp'),
  saveSmtp:           (data) => request('/settings/smtp', { method: 'POST', body: JSON.stringify(data) }),
  testSmtp:           (testEmail) => request('/settings/smtp/test', { method: 'POST', body: JSON.stringify({ test_email: testEmail }) }),
  checkSmtpHealth:    () => request('/settings/smtp/health'),
  detectSmtp:         (domain) => request('/settings/smtp/detect', { method: 'POST', body: JSON.stringify({ domain }) }),
  getAdmin:           () => request('/settings/admin'),
  saveAdmin:          (data) => request('/settings/admin', { method: 'POST', body: JSON.stringify(data) }),
  getSending:         () => request('/settings/sending'),
  setSending:         (paused) => request('/settings/sending', { method: 'POST', body: JSON.stringify({ paused }) }),
  sendDailyReport:    () => request('/settings/reports/daily',    { method: 'POST' }),
  sendBirthdayReport: () => request('/settings/reports/birthday', { method: 'POST' }),
  sendTrackingReport: () => request('/settings/reports/tracking', { method: 'POST' }),
  sendWeeklyReport:   () => request('/settings/reports/weekly',   { method: 'POST' }),
  getProfile:         () => request('/settings/profile'),
  updateProfile:  (data) => request('/settings/profile', { method: 'PUT', body: JSON.stringify(data) }),
  changePassword: (data) => request('/settings/password', { method: 'PUT', body: JSON.stringify(data) }),
  deleteAccount:  (confirm) => request('/settings/account', { method: 'DELETE', body: JSON.stringify({ confirm }) }),
  getPlan:        () => request('/settings/plan'),

  // ── Personal / Family Profile ─────────────────────────────────────────────
  getPersonalProfile:  ()     => request('/settings/personal'),
  savePersonalProfile: (data) => request('/settings/personal', { method: 'PUT', body: JSON.stringify(data) }),

  // ── Landing Pages ─────────────────────────────────────────────────────────
  getLandingPages:       ()       => request('/landing-pages'),
  createLandingPage:     (data)   => request('/landing-pages',      { method: 'POST',   body: JSON.stringify(data) }),
  updateLandingPage:     (id, d)  => request(`/landing-pages/${id}`,{ method: 'PUT',    body: JSON.stringify(d) }),
  deleteLandingPage:     (id)     => request(`/landing-pages/${id}`,{ method: 'DELETE' }),
  publishLandingPage:    (id)     => request(`/landing-pages/${id}/publish`,   { method: 'POST' }),
  unpublishLandingPage:  (id)     => request(`/landing-pages/${id}/unpublish`, { method: 'POST' }),
  getLandingPageStats:   (id)     => request(`/landing-pages/${id}/stats`),
  getLandingPageLeads:   (id)     => request(`/landing-pages/${id}/leads`),
  drawLandingPageWinner: (id)     => request(`/landing-pages/${id}/draw-winner`, { method: 'POST' }),
  landingWinnerAction:   (id, action) => request(`/landing-pages/${id}/winner-action`, { method: 'POST', body: JSON.stringify({ action }) }),
  redeemCoupon:          (id, code)   => request(`/landing-pages/${id}/redeem`, { method: 'POST', body: JSON.stringify({ code }) }),
  weaveMemory:           (note, subject) => request('/ai/weave-memory', { method: 'POST', body: JSON.stringify({ note, subject }) }),

  // ── Collaborators ─────────────────────────────────────────────────────────
  getCollaborators:    ()         => request('/settings/collaborators'),
  createCollaborator:  (data)     => request('/settings/collaborators', { method: 'POST', body: JSON.stringify(data) }),
  updateCollaborator:  (id, data) => request(`/settings/collaborators/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  deleteCollaborator:  (id)       => request(`/settings/collaborators/${id}`, { method: 'DELETE' }),
};
